import type {
  InstitutionalHolderRecord,
  InstitutionalHoldersFileResponse,
} from '@/services/api';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';
import { formatInstitutionalSnapshotPeriod } from '@/utils/institutionalSnapshotPeriod';

export type FilingPeriodCardAction = 'new' | 'added' | 'reduced' | 'closed' | 'held';

export interface FilingPeriodCard {
  /** Stable key for list rendering (CIK + position type). */
  positionKey: string;
  cik: string;
  /** Full institution name for detail tooltip. */
  fullName: string;
  name: string;
  displayShares: number;
  /** Quarter-end ownership % (ownership mode). */
  displayOwnership?: number;
  action: FilingPeriodCardAction;
}

export interface FilingPeriodForChart {
  quarterKey: string;
  quarterLabel: string;
  /** Quarter-end ISO date from FMP. */
  quarterDate: string;
  /** Calendar quarter start (Q1=Jan 1, Q2=Apr 1, Q3=Jul 1, Q4=Oct 1). */
  quarterStartDate: string;
  /** First trading day on the chart in this calendar quarter (≥ quarter start). */
  chartStartDate: string;
  /** Last trading day on the chart in this calendar quarter (≤ quarter end). */
  chartDate: string;
  institutions: FilingPeriodCard[];
}

/** Vertical divider at a quarter end (may exist without a holder column). */
export interface QuarterEndChartReference {
  quarterKey: string;
  chartDate: string;
}

export interface InstitutionalChartOverlayData {
  filingPeriods: FilingPeriodForChart[];
  quarterEndReferenceLines: QuarterEndChartReference[];
}

function parseQuarterSortKey(key: string): number {
  const m = key.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return 0;
  return parseInt(m[1]!, 10) * 100 + parseInt(m[2]!, 10);
}

function sortQuarterKeysDesc(keys: string[]): string[] {
  return [...keys].sort((a, b) => parseQuarterSortKey(b) - parseQuarterSortKey(a));
}

const CARD_NAME_MAX_LEN = 22;

/** 13F line type from FMP `putCallShare` (Share / Call / Put). */
export function formatPositionTypeLabel(putCallShare?: string | null): string | null {
  const v = (putCallShare ?? '').trim().toLowerCase();
  if (v === 'share') return 'Shares';
  if (v === 'call') return 'Calls';
  if (v === 'put') return 'Puts';
  return null;
}

/** Abbreviate long institution names for chart cards. */
export function shortenInstitutionName(name: string, maxLen = CARD_NAME_MAX_LEN): string {
  const substitutions: [RegExp, string][] = [
    [/\bMANAGEMENT\b/gi, 'MGMT'],
    [/\bINVESTMENT\b/gi, 'INV'],
    [/\bADVISERS?\b/gi, 'ADVS'],
    [/\bASSOCIATES\b/gi, 'ASSOC'],
    [/\bPARTNERS\b/gi, 'PTRS'],
    [/\bCAPITAL\b/gi, 'CAP'],
    [/, L\.?P\.?$/gi, ''],
    [/, LLC\.?$/gi, ''],
    [/, INC\.?$/gi, ''],
  ];
  let s = name;
  for (const [re, rep] of substitutions) s = s.replace(re, rep);
  s = s.trim();
  if (maxLen <= 0) return '…';
  return s.length > maxLen ? `${s.slice(0, Math.max(1, maxLen - 1))}…` : s;
}

/** FMP sometimes returns investorName as ""; fall back to CIK label. */
export function formatInstitutionalHolderDisplayName(
  holder: Pick<InstitutionalHolderRecord, 'investorName' | 'cik'>
): string {
  const raw = (holder.investorName ?? '').trim();
  if (raw) return shortenInstitutionName(raw);
  const cikDigits = (holder.cik ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (cikDigits) return shortenInstitutionName(`CIK ${cikDigits}`);
  return 'Unknown';
}

/** Ribbon card label: shortened institution name + Shares / Calls / Puts. */
export function formatInstitutionalHolderCardName(
  holder: Pick<InstitutionalHolderRecord, 'investorName' | 'cik' | 'putCallShare'>
): string {
  const raw = (holder.investorName ?? '').trim();
  const cikDigits = (holder.cik ?? '').replace(/\D/g, '').replace(/^0+/, '');
  const source = raw || (cikDigits ? `CIK ${cikDigits}` : 'Unknown');
  const positionLabel = formatPositionTypeLabel(holder.putCallShare);
  if (!positionLabel) return shortenInstitutionName(source);
  const suffix = ` ${positionLabel}`;
  return `${shortenInstitutionName(source, CARD_NAME_MAX_LEN - suffix.length)}${suffix}`;
}

export function formatInstitutionalHolderFullTitle(
  holder: Pick<InstitutionalHolderRecord, 'investorName' | 'cik' | 'putCallShare'>
): string {
  const raw = (holder.investorName ?? '').trim();
  const base =
    raw ||
    ((holder.cik ?? '').replace(/\D/g, '').replace(/^0+/, '')
      ? `CIK ${(holder.cik ?? '').replace(/\D/g, '').replace(/^0+/, '')}`
      : 'Unknown institution');
  const positionLabel = formatPositionTypeLabel(holder.putCallShare);
  return positionLabel ? `${base} (${positionLabel})` : base;
}

type ChartInstitutionSortable = FilingPeriodCard & { sortDelta: number };

/**
 * All quarter movers for the chart ribbon: largest share adds first, largest sells last.
 * New positions count full position size; unchanged holders are omitted.
 */
export function buildChartInstitutionsForQuarter(
  holders: InstitutionalHolderRecord[]
): FilingPeriodCard[] {
  const cards: ChartInstitutionSortable[] = [];

  for (const h of holders) {
    const name = formatInstitutionalHolderCardName(h);
    const positionKey = holderPositionKey(h);
    const cik = (h.cik ?? '').trim();
    const fullName = formatInstitutionalHolderFullTitle(h);

    if (h.isNew && (h.sharesNumber ?? 0) > 0) {
      const shares = h.sharesNumber ?? 0;
      cards.push({
        positionKey,
        cik,
        fullName,
        name,
        displayShares: shares,
        action: 'new',
        sortDelta: shares,
      });
      continue;
    }

    const change = h.changeInSharesNumber ?? 0;
    if (change > 0) {
      cards.push({
        positionKey,
        cik,
        fullName,
        name,
        displayShares: change,
        action: 'added',
        sortDelta: change,
      });
      continue;
    }

    if (change < 0 || h.isSoldOut) {
      const soldShares =
        change < 0 ? Math.abs(change) : (h.sharesNumber ?? 0);
      if (soldShares <= 0) continue;
      cards.push({
        positionKey,
        cik,
        fullName,
        name,
        displayShares: soldShares,
        action: h.isSoldOut ? 'closed' : 'reduced',
        sortDelta: change < 0 ? change : -soldShares,
      });
    }
  }

  return cards
    .sort((a, b) => b.sortDelta - a.sortDelta)
    .map(({ sortDelta: _sortDelta, ...card }) => card);
}

type OwnershipSortable = FilingPeriodCard & { sortOwnership: number };

/**
 * Top holders at quarter-end by reported ownership %.
 * Omits open positions with 0% ownership (FMP often lists tiny lots with ownership 0).
 */
export function buildChartOwnershipInstitutionsForQuarter(
  holders: InstitutionalHolderRecord[]
): FilingPeriodCard[] {
  const cards: OwnershipSortable[] = [];

  for (const h of holders) {
    const shares = h.sharesNumber ?? 0;
    const ownership = h.ownership ?? 0;
    if (shares <= 0 || h.isSoldOut || ownership <= 0) continue;

    const name = formatInstitutionalHolderCardName(h);
    const positionKey = holderPositionKey(h);
    const cik = (h.cik ?? '').trim();
    const fullName = formatInstitutionalHolderFullTitle(h);

    cards.push({
      positionKey,
      cik,
      fullName,
      name,
      displayShares: shares,
      displayOwnership: ownership,
      action: 'held',
      sortOwnership: ownership,
    });
  }

  return cards
    .sort((a, b) => b.sortOwnership - a.sortOwnership)
    .map(({ sortOwnership: _sortOwnership, ...card }) => card);
}

function buildInstitutionsForQuarter(
  holders: InstitutionalHolderRecord[],
  displayMode: InstitutionalHoldersChartDisplayMode
): FilingPeriodCard[] {
  return displayMode === 'ownership'
    ? buildChartOwnershipInstitutionsForQuarter(holders)
    : buildChartInstitutionsForQuarter(holders);
}

/** Calendar quarter start ISO (Q1=Jan 1, Q2=Apr 1, Q3=Jul 1, Q4=Oct 1). */
export function calendarQuarterStartIso(quarterKey: string): string | null {
  const m = quarterKey.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  const month = (parseInt(m[2]!, 10) - 1) * 3 + 1;
  return `${m[1]}-${String(month).padStart(2, '0')}-01`;
}

/** Map quarter-end to last chart date on or before that day (chart dates ascending). */
export function snapQuarterEndToChartDate(
  quarterEndIso: string,
  chartDatesAsc: string[]
): string | null {
  if (chartDatesAsc.length === 0) return null;
  let best: string | null = null;
  for (const d of chartDatesAsc) {
    if (d <= quarterEndIso) best = d;
    else break;
  }
  return best;
}

/** Map quarter-start to first chart date on or after that day (chart dates ascending). */
export function snapQuarterStartToChartDate(
  quarterStartIso: string,
  chartDatesAsc: string[]
): string | null {
  for (const d of chartDatesAsc) {
    if (d >= quarterStartIso) return d;
  }
  return null;
}

export function buildInstitutionalChartOverlayData(
  data: InstitutionalHoldersFileResponse,
  chartDatesAsc: string[],
  displayMode: InstitutionalHoldersChartDisplayMode = 'activity'
): InstitutionalChartOverlayData {
  const quarters = data.quarters;
  if (!quarters || chartDatesAsc.length === 0) {
    return { filingPeriods: [], quarterEndReferenceLines: [] };
  }

  const visibleSet = new Set(chartDatesAsc);
  const keys = sortQuarterKeysDesc(Object.keys(quarters)).reverse();
  const firstVisibleDate = chartDatesAsc[0]!;

  const filingPeriods: FilingPeriodForChart[] = [];
  const quarterEndReferenceLines: QuarterEndChartReference[] = [];

  for (const k of keys) {
    const holders = quarters[k]?.holders ?? [];
    if (holders.length === 0 || !holders[0]?.date) continue;

    const quarterDate = holders[0]!.date!;
    const quarterStartDate = calendarQuarterStartIso(k);
    if (!quarterStartDate) continue;

    const chartDate = snapQuarterEndToChartDate(quarterDate, chartDatesAsc);
    const chartStartDate = snapQuarterStartToChartDate(quarterStartDate, chartDatesAsc);
    if (!chartDate || !chartStartDate || !visibleSet.has(chartDate)) continue;
    if (chartStartDate > chartDate) continue;

    quarterEndReferenceLines.push({ quarterKey: k, chartDate });

    // Skip holder columns for quarters clipped at the left edge of the chart window.
    if (quarterStartDate < firstVisibleDate) continue;

    const institutions = buildInstitutionsForQuarter(holders, displayMode);
    if (institutions.length === 0) continue;

    const m = k.match(/^(\d{4})-Q([1-4])$/);
    const quarterLabel = m ? `Q${m[2]} ${m[1]}` : k;

    filingPeriods.push({
      quarterKey: k,
      quarterLabel,
      quarterDate,
      quarterStartDate,
      chartStartDate,
      chartDate,
      institutions,
    });
  }

  return { filingPeriods, quarterEndReferenceLines };
}

export function buildFilingPeriodsForChart(
  data: InstitutionalHoldersFileResponse,
  chartDatesAsc: string[],
  displayMode: InstitutionalHoldersChartDisplayMode = 'activity'
): FilingPeriodForChart[] {
  return buildInstitutionalChartOverlayData(data, chartDatesAsc, displayMode).filingPeriods;
}

/** Primary label on chart ribbon cards. */
export function institutionalHolderCardMetricLabel(
  inst: FilingPeriodCard,
  displayMode: InstitutionalHoldersChartDisplayMode
): string {
  if (displayMode === 'ownership' || inst.action === 'held') {
    if (inst.displayOwnership != null && inst.displayOwnership > 0) {
      return `${inst.displayOwnership.toFixed(2)}%`;
    }
    return formatInstitutionalChartShares(inst.displayShares);
  }
  const isNegative = inst.action === 'reduced' || inst.action === 'closed';
  if (inst.action === 'new') {
    return `NEW · ${formatInstitutionalChartShares(inst.displayShares)}`;
  }
  if (inst.action === 'closed') return 'Sold out';
  return `${isNegative ? '−' : '+'}${formatInstitutionalChartShares(inst.displayShares)}`;
}

export function institutionalHolderCardIsNegative(inst: FilingPeriodCard): boolean {
  return inst.action === 'reduced' || inst.action === 'closed';
}

export function formatInstitutionalChartShares(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/** Re-export for table section — holder position key. */
export function holderPositionKey(h: InstitutionalHolderRecord): string {
  const cik = h.cik?.trim() ?? '';
  const putCall = (h.putCallShare ?? '').trim();
  const secType = (h.typeOfSecurity ?? '').trim();
  return `${cik}|${putCall}|${secType}`;
}

export interface TopInstitutionalHolderRow {
  positionKey: string;
  cik: string;
  fullName: string;
  institutionName: string;
  positionTypeLabel: string | null;
  shares: number;
  ownership: number;
  marketValue: number;
}

export interface LatestQuarterTopHolders {
  quarterKey: string;
  quarterLabel: string;
  snapshotPeriod: string;
  holders: TopInstitutionalHolderRow[];
}

/** Largest open 13F positions from the most recent quarter in the payload. */
export function buildLatestQuarterTopHolders(
  data: InstitutionalHoldersFileResponse | null | undefined,
  limit = 12
): LatestQuarterTopHolders | null {
  const quarters = data?.quarters;
  if (!quarters || typeof quarters !== 'object') return null;

  const sortedKeys = sortQuarterKeysDesc(Object.keys(quarters));
  const latestKey = sortedKeys.find((k) => (quarters[k]?.holders?.length ?? 0) > 0);
  if (!latestKey) return null;

  const rows: TopInstitutionalHolderRow[] = [];
  for (const h of quarters[latestKey]!.holders ?? []) {
    const shares = h.sharesNumber ?? 0;
    if (shares <= 0 || h.isSoldOut) continue;
    rows.push({
      positionKey: holderPositionKey(h),
      cik: (h.cik ?? '').trim(),
      fullName: formatInstitutionalHolderFullTitle(h),
      institutionName: formatInstitutionalHolderDisplayName(h),
      positionTypeLabel: formatPositionTypeLabel(h.putCallShare),
      shares,
      ownership: h.ownership ?? 0,
      marketValue: h.marketValue ?? 0,
    });
  }

  rows.sort((a, b) => {
    const byShares = b.shares - a.shares;
    if (byShares !== 0) return byShares;
    return b.marketValue - a.marketValue;
  });

  const m = latestKey.match(/^(\d{4})-Q([1-4])$/);
  const quarterLabel = m ? `Q${m[2]} ${m[1]}` : latestKey;

  return {
    quarterKey: latestKey,
    quarterLabel,
    snapshotPeriod: formatInstitutionalSnapshotPeriod(latestKey),
    holders: rows.slice(0, Math.max(1, limit)),
  };
}

export function formatInstitutionalMarketValueUsd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}
